import React, { useState, useEffect } from 'react'
import { token } from '@atlaskit/tokens'
import Avatar from '@atlaskit/avatar'
import { formatRelativeTime } from '../../utils/formatters'
import { getUserDetails, getCachedUserDetails } from '../../utils/userCache'

/**
 * AUDIT LOG ITEM COMPONENT
 *
 * Displays a single audit log entry with user info, action, and timestamp.
 *
 * Supports action types:
 * - CREATED: Initial status set when execution created
 * - UPDATED: Status changed to new value
 * - BUG_LINKED: Jira bug linked to execution
 * - BUG_UNLINKED: Jira bug unlinked from execution
 *
 * @param {object} entry - Audit log entry from API
 */
export function AuditLogItem({ entry }) {
  // State for user details (fetched from Jira)
  const [userDetails, setUserDetails] = useState(() => {
    // Try to get from cache first for immediate render
    return getCachedUserDetails(entry?.changedByAccountId)
  })

  // Extract author accountId from entry
  const authorAccountId = entry?.changedByAccountId

  /**
   * FETCH USER DETAILS
   * Fetches user details from Jira API on mount.
   */
  useEffect(() => {
    if (authorAccountId) {
      getUserDetails(authorAccountId).then(details => {
        if (details) {
          setUserDetails(details)
        }
      })
    }
  }, [authorAccountId])

  /**
   * GET ACTION MESSAGE
   * Returns a formatted message based on the action type.
   */
  const getActionMessage = () => {
    switch (entry.action) {
      case 'CREATED':
        return (
          <span>
            Execution created with status{' '}
            <StatusBadge
              name={entry.newStatusName}
              color={entry.newStatusColor}
            />
          </span>
        )
      case 'UPDATED':
        return (
          <span>
            Changed status from{' '}
            <StatusBadge
              name={entry.previousStatusName}
              color={entry.previousStatusColor}
            />
            {' → '}
            <StatusBadge
              name={entry.newStatusName}
              color={entry.newStatusColor}
            />
          </span>
        )
      case 'BUG_LINKED':
        return (
          <span>
            Linked bug{' '}
            <span
              style={{
                fontWeight: 600,
                color: token('color.link', '#0052CC')
              }}
            >
              {entry.jiraIssueKey}
            </span>
          </span>
        )
      case 'BUG_UNLINKED':
        return (
          <span>
            Unlinked bug{' '}
            <span
              style={{
                fontWeight: 500,
                color: token('color.text', '#172B4D')
              }}
            >
              {entry.jiraIssueKey}
            </span>
          </span>
        )
      default:
        return <span>{entry.action}</span>
    }
  }

  // Determine display name and avatar
  const displayName = userDetails?.displayName || entry.changedByDisplayName || 'Unknown User'
  const avatarUrl = userDetails?.avatarUrl || null

  return (
    <div
      className="audit-log-item"
      style={{
        padding: '12px',
        borderRadius: '4px',
        backgroundColor: token('color.background.neutral', '#F4F5F7'),
        marginBottom: '8px'
      }}
    >
      {/* Header: Avatar + Name + Timestamp */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '6px'
        }}
      >
        {/* Left: Avatar + Name */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Avatar
            size="small"
            src={avatarUrl}
            name={displayName}
          />
          <span
            style={{
              fontSize: '13px',
              fontWeight: 500,
              color: token('color.text', '#172B4D')
            }}
          >
            {displayName}
          </span>
        </div>

        {/* Right: Timestamp */}
        <span
          style={{
            fontSize: '12px',
            color: token('color.text.subtle', '#5E6C84')
          }}
        >
          {formatRelativeTime(entry.changedOn)}
        </span>
      </div>

      {/* Action message */}
      <div
        style={{
          fontSize: '13px',
          color: token('color.text', '#172B4D'),
          lineHeight: '1.5',
          paddingLeft: '32px' // Align with text after avatar
        }}
      >
        {getActionMessage()}
      </div>
    </div>
  )
}

/**
 * GET STATUS BACKGROUND COLOR
 * Maps status names to Atlaskit semantic tokens for consistent colors.
 * Uses color.background.*.bold tokens which are designed for solid backgrounds.
 * This ensures audit log status badges match the StatusSelector component colors.
 *
 * @param {string} statusName - The status name (Pass, Fail, Block, Query, Exclude)
 * @param {string} statusColor - Fallback color from API if status name not recognized
 * @returns {string} The background color to use for the status badge
 */
function getStatusBackgroundColor(statusName, statusColor) {
  const name = statusName?.toLowerCase()

  // Map known status names to Atlaskit semantic tokens
  if (name === 'pass' || name === 'passed') return token('color.background.success.bold', '#1F845A')
  if (name === 'fail' || name === 'failed') return token('color.background.danger.bold', '#CA3521')
  if (name === 'block' || name === 'blocked') return token('color.icon.warning', '#E06C00')
  if (name === 'query') return token('color.background.information.bold', '#0055CC')
  if (name === 'exclude' || name === 'excluded') return token('color.background.neutral.bold', '#626F86')
  // Cleared uses light neutral background (not bold) - same as StatusSelector
  if (name === 'cleared') return token('color.background.neutral', '#DFE1E6')

  // Fallback to status color from API, or neutral gray
  if (statusColor) return statusColor
  return token('color.background.neutral.bold', '#626F86')
}

/**
 * STATUS BADGE COMPONENT
 * Inline status badge for audit log entries.
 * Uses semantic tokens for known statuses to match StatusSelector component.
 */
function StatusBadge({ name, color }) {
  // Use semantic tokens for known statuses, fallback to API color
  const bgColor = getStatusBackgroundColor(name, color)

  // Cleared status uses dark text (light background), all others use white (dark backgrounds)
  const isCleared = name?.toLowerCase() === 'cleared'
  const textColor = isCleared ? token('color.text', '#172B4D') : '#FFFFFF'

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: '12px',
        backgroundColor: bgColor,
        color: textColor,
        fontSize: '11px',
        fontWeight: 600
      }}
    >
      {name || 'Unknown'}
    </span>
  )
}

export default AuditLogItem
